import Feed, { isProductItem, normalizeProductItem } from "@models/feed";
import Product from "@models/product";
import { authStore } from "../stores/main";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import Cart from "@models/cart";
import Order from "@models/order";
import PaySession from "@models/pay-session";
import { OrderItem } from "@models/order";
import { globalPopupStore } from "@stores/main";
import { getStoredCountry, getStoredCurrency } from "../utils/cookieUtils";
import Coupon from "@models/coupon";
import { PaymentMethodsResponse } from "@models/payment-method";
import {
  normalizeMyOrdersPayload,
  resolveProductIdFromOrderRow,
} from "@utils/orderItemProductId";

export type StoreAPIResponse = {
  code: number;
  msg: string;
  response: any;
  status: "success" | "error";
};

export type AxiosResponseWithStoreEnvelope<T = unknown> = AxiosResponse<T> & {
  storeApiEnvelope?: StoreAPIResponse;
};

/** Normalized store mutation outcome; `ok` is true only when API `code === 200`. */
export type StoreMutationApiResult = {
  ok: boolean;
  code: number | undefined;
  message: string;
  response: AxiosResponse<unknown>;
};

/** @alias StoreMutationApiResult */
export type AddToCartApiResult = StoreMutationApiResult;
/** @alias StoreMutationApiResult */
export type AddToWishlistApiResult = StoreMutationApiResult;

function parseStoreMutationResponse(
  res: AxiosResponse<unknown>
): StoreMutationApiResult {
  const ext = res as AxiosResponseWithStoreEnvelope;
  const envelope = ext.storeApiEnvelope;
  const code = envelope?.code;
  const ok = code === 200;

  const readMessage = (obj: unknown): string => {
    if (!obj || typeof obj !== "object" || !("message" in obj)) return "";
    const m = (obj as { message?: unknown }).message;
    return typeof m === "string" && m.trim() !== "" ? m.trim() : "";
  };
  const fromResponseBody = readMessage(res.data);
  const fromEnvelopeResponse = readMessage(envelope?.response);
  const fromTopMsg =
    typeof envelope?.msg === "string" && envelope.msg.trim() !== ""
      ? envelope.msg.trim()
      : "";
  const message =
    fromResponseBody || fromEnvelopeResponse || fromTopMsg || "";
  return { ok, code, message, response: res };
}

/** Raw item from get_product_groups API response.products (snake_case) */
type RawProductGroupItem = {
  product_id?: string;
  best_buy_price_string?: string;
  best_buy_price?: number;
  title?: string;
  extended_title?: string;
  selected?: boolean;
  [key: string]: any;
};

/** Item from get_product_groups API (response.products) for Select Amount section */
export type ProductGroupItem = {
  product_id?: string;
  buy_price_string?: string;
  buy_price?: number;
  label?: string;
  selected?: boolean;
  [key: string]: any;
};

/** Deep plain copy so DevTools can expand logged API payloads reliably. */
function devConsoleSnapshot(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  try {
    return JSON.parse(JSON.stringify(value)) as unknown;
  } catch {
    return value;
  }
}

export default class StoresAPI {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_STORE_API_URL,
      timeout: 30000,
    });

    this.client.interceptors.request.use((config) => {
      // Add country and currency headers to all requests
      const country = getStoredCountry();
      const currency = getStoredCurrency();
      
      if (!country || !currency) {
        console.warn('Country or currency cookies not found in API request');
      }
      
      if (country) {
        config.headers.set('country', country);
      }
      if (currency) {
        config.headers.set('currency', currency);
      }

      if (authStore.isAuthenticated) {
        const data: FormData = config.data ?? new FormData();
        const { userid, token } = authStore.userIdAndToken;
        data.append("user_id", userid);
        data.append("session_token", token);
        config.data = data;
      }

      return config;
    });

    this.client.interceptors.response.use(
      (response: AxiosResponse<StoreAPIResponse>) => {
        // If the response is a blob, don't modify it
        if (response.config.responseType === 'blob') {
          return response;
        }
        const envelope = response.data;
        if (
          envelope &&
          typeof envelope === "object" &&
          "response" in envelope &&
          "code" in envelope
        ) {
          (response as AxiosResponseWithStoreEnvelope).storeApiEnvelope =
            envelope as StoreAPIResponse;
          response.data = (envelope as StoreAPIResponse).response;
        }
        return response;
      },
      (error: AxiosError<StoreAPIResponse>) => {
        // if(error.response?.status === 403)
        // {
        //   globalPopupStore.show(error.response.data.response.message || 'Access forbidden');
        // }
        if (error.response?.status === 401) {
          authStore.logout();
        }


        if (error.response?.status !== 401 && error.response?.data?.msg) {
          globalPopupStore.show(error.response.data.msg);
        }

        return Promise.reject({
          code: error.response?.status ?? 500,
          message: error.response?.data?.msg ?? "Unknown error",
        });
      }
    );
  }

  async getHomeFeed() {
    try {
      const res = await this.client.post<{ rails: any[] }>(
        "/v1/content/feed/home"
      );
      // If rails is not present or empty, return empty array to trigger fallback
      if (!res.data || !res.data.rails || res.data.rails.length === 0) {
        return [];
      }
      return res.data.rails.map((r) => new Feed(r));
    } catch (error) {
      // If API call fails, return empty array to trigger fallback to static banner
      console.error('Error fetching home feed:', error);
      return [];
    }
  }

  async getFeedList(): Promise<Feed[]> {
    try {
      const res = await this.client.post<{ rails: any[] }>(
        "/v1/content/feed/list"
      );
      if (!res.data || !res.data.rails || res.data.rails.length === 0) {
        return [];
      }
      return res.data.rails.map((r) => new Feed(r));
    } catch (error) {
      console.error("Error fetching feed list:", error);
      return [];
    }
  }

  async getFeedExpand(feedId: string | number): Promise<Product[]> {
    try {
      const res = await this.client.post<{ items?: any[] }>(
        `/v1/content/feed/expand/${feedId}`
      );
      const items = res.data?.items;
      if (!Array.isArray(items)) {
        return [];
      }
      return items
        .filter(isProductItem)
        .map((item: { [x: string]: any }) => new Product(normalizeProductItem(item)));
    } catch (error) {
      console.error("Error fetching feed expand:", error);
      return [];
    }
  }

  async searchProducts(
    query: string,
    page: number,
    filters?: { categories?: string[] },
    orderBy?: string
  ) {
    const data = new FormData();
    data.append("q", `${query}`);
    data.append("page", `${page}`);

    // Add filters if provided
    if (filters && filters.categories && filters.categories.length > 0) {
      const categoriesStr =
        filters.categories.length === 1 && filters.categories[0] === 'ecommerce'
          ? 'ecommerce,e-commerce'
          : filters.categories.join(',');
      const filtersJson = JSON.stringify({
        categories: categoriesStr,
      });
      data.append("filters", filtersJson);
    }

    // Add order_by if provided
    if (orderBy) {
      data.append("order_by", orderBy);
    }

    const res = await this.client.post<{
      page: number;
      results: any[];
      total_items: number;
    }>("/v1/content/search", data);

    return {
      page: res.data.page,
      total: res.data.total_items,
      items: res.data.results.map((r) => new Product(r)),
    };
  }

  async getProduct(id: string) {
    const res = await this.client.post<{
      info: any;
      is_already_purchased: boolean;
      last_purchased_date: number;
      more_products_for_same_group: any[];
      show_steam_gc_as_main: boolean;
      similar_products: {
        title: string;
        items: any[];
      };
      coupons?: {
        all_offers: any[];
      };
    }>(`/v1/content/details/info/${id}`);

    const data = res.data;
    let steamGiftCardOffers = null;

    if (data.show_steam_gc_as_main) {
      steamGiftCardOffers = await this.getSteamGiftCardOffers(id);
    }

    // Extract and map coupons
    const coupons = data.coupons?.all_offers
      ? data.coupons.all_offers.map((coupon: any) => new Coupon(coupon))
      : [];

    const productInfo = new Product({
      ...data.info,
      similar_products: data.similar_products
    });
    
    // Preserve redemption_details from raw data as it's not in Product model
    (productInfo as any).redemption_details = data.info.redemption_details;

    return {
      info: productInfo,
      isAlreadyPurchased: data.is_already_purchased,
      lastPurchasedDate: data.last_purchased_date
        ? new Date(data.last_purchased_date * 1000)
        : null,
      moreProductsForSameGroup: data.more_products_for_same_group.map(
        (p) => new Product(p)
      ),
      showSteamGcAsMain: data.show_steam_gc_as_main,
      steamGiftCardOffers,
      coupons
    };
  }

  /** Get product groups/variants for Select Amount (e.g. gift card denominations). POST /v1/content/get_product_groups/:productId. Response: { products: [...] } */
  async getProductGroups(productId: string): Promise<ProductGroupItem[]> {
    const res = await this.client.post<{ products?: RawProductGroupItem[] }>(
      `/v1/content/get_product_groups/${productId}`
    );
    const products = res.data?.products;
    if (!Array.isArray(products) || products.length === 0) return [];
    return products.map((p) => ({
      product_id: p.product_id,
      buy_price_string: p.best_buy_price_string,
      buy_price: p.best_buy_price,
      label: p.title ?? p.extended_title ?? p.best_buy_price_string,
      selected: p.selected,
    }));
  }

  async getGameProducts(gameId: string) {
    const res = await this.client.post<{
      all_products: any[];
      recommended_product: any | null;
      store_redirect_url: string | null;
    }>(`/v1/content/details/deals/oplayid/${gameId}`);

    const data = res.data;

    return {
      allProducts: data.all_products.map((p) => new Product(p)),
      recommendedProduct: data.recommended_product
        ? new Product(data.recommended_product)
        : null,
      storeRedirectUrl: data.store_redirect_url,
    };
  }

  async addToWishlist(productId: string): Promise<AddToWishlistApiResult> {
    const data = new FormData();
    data.append("product_id", productId);
    const res = await this.client.post("/v1/store/wishlist/add", data);
    return parseStoreMutationResponse(res);
  }

  async removeFromWishlist(productId: string) {
    const data = new FormData();
    data.append("product_id", productId);
    await this.client.post("/v1/store/wishlist/remove", data);
  }

  async getWishlist() {
    const res = await this.client.post<{
      items: any[];
    }>("/v1/store/wishlist/view");

    return res.data.items.map((i) => new Product(i));
  }

  async getOrders() {
    const res = await this.client.post<unknown>("/v1/store/my_orders/view");
    const rows = normalizeMyOrdersPayload(res.data);
    return rows.map((item) => {
      const row = item as unknown as Record<string, unknown>;
      const product_id = resolveProductIdFromOrderRow(row);
      return product_id ? { ...(item as OrderItem), product_id } : (item as OrderItem);
    });
  }

  async getRedeemKey(redemptionId: string) {
    const res = await this.client.post<{
      key: string | { [key: string]: string };
    }>(`/v1/store/my_orders/redeem/${redemptionId}`);

    return res.data.key;
  }

  async getCart() {
    const res = await this.client.post("/v1/store/cart/view");
    return new Cart(res.data);
  }

  async addToCart(productId: string): Promise<AddToCartApiResult> {
    const data = new FormData();
    data.append("product_id", productId);
    const res = await this.client.post("/v1/store/cart/add", data);
    return parseStoreMutationResponse(res);
  }

  async removeFromCart(productId: string, quantity?: number) {
    const data = new FormData();
    data.append("product_id", productId);
    if (quantity) {
      data.append("quantity", String(quantity));
    }
    await this.client.post("/v1/store/cart/remove", data);
  }

  async getSteamGiftCardOffers(productId: string) {
    const data = new FormData();
    data.append("product_id", productId);
    const res = await this.client.post("/v1/content/offers/steam_gift_cards", data);
    return res.data;
  }

  async checkout(couponCode?: string) {
    const data = new FormData();
    if (couponCode) {
      data.append("coupon_code", couponCode);
    }
    const res = await this.client.post("/v1/store/transaction/checkout", data);
    return new Order(res.data);
  }

  async createPaySession(
    orderSessionId: string,
    provider: string,
    method: string
  ): Promise<{ raw: unknown; session: PaySession }> {
    const data = new FormData();
    data.append("provider", provider);
    data.append("method", method);
    const res = await this.client.post(
      `/v1/store/transaction/create_pay_session/${orderSessionId}`,
      data,
      { timeout: 30000 }
    );
    const raw = res.data;
    const session = new PaySession(raw as { [x: string]: any });
    const logCreatePaySession =
      process.env.NODE_ENV !== "production" || process.env.APP_ENV === "qa";
    if (logCreatePaySession) {
      const rawSnapshot = devConsoleSnapshot(raw);
      const parsedLog = {
        state: session.state,
        globalMessage: session.globalMessage,
        redirectPaymentUrl: session.redirectPaymentUrl,
        refreshCheckoutPage: session.refreshCheckoutPage,
        enforceKyc: session.enforceKyc,
      };
      const useGroup = typeof console.groupCollapsed === "function";
      if (useGroup) console.groupCollapsed("[create_pay_session]");
      try {
        console.log("orderSessionId:", orderSessionId);
        console.log("provider:", provider);
        console.log("method:", method);
        console.log("raw:", rawSnapshot);
        console.log("parsed:", parsedLog);
        try {
          console.log("raw (JSON)\n" + JSON.stringify(rawSnapshot, null, 2));
        } catch {
          console.log("raw (JSON): <not serializable>");
        }
        console.log("parsed (JSON)\n" + JSON.stringify(parsedLog, null, 2));
      } finally {
        if (useGroup) console.groupEnd();
      }
    }
    return { raw, session };
  }

  async downloadInvoice(orderId: string, itemId: string): Promise<Blob> {
    const data = new FormData();
    const response = await this.client.post(`/v1/store/my_orders/invoice/${orderId}/${itemId}`, data, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/pdf',
      },
    });
    return response.data as Blob;
  }

  async getCurrencyList() {
    const res = await this.client.get<{
      currency: string;
      symbol: string;
    }[]>("/v1/general/global/currency_list");
    
    return res.data;
  }

  async getCountryList() {
    const res = await this.client.get<{
      country: string;
    }[]>("/v1/general/global/country_filters");
    
    return res.data;
  }

  async getDefaultLocale() {
    const res = await this.client.get<{
      country: string;
      currency: string;
      symbol: string;
    }>("/v1/general/default/locale");
    
    return res.data;
  }

  async getPayemntMethds(orderId: string): Promise<PaymentMethodsResponse> {
    const data = new FormData();
    data.append("order_id", orderId);
    const response = await this.client.post<PaymentMethodsResponse>("/v1/store/transaction/get_payment_methods", data);
    return response.data;
  }
}
